import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Divider,
  Grid,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudDownload as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import ImportProgressTracker from '../Components/ImportProgressTracker';
import ApproveImportModal from '../Components/ApproveImportModal';
import RejectImportModal from '../Components/RejectImportModal';
import SuccessModal from '../Components/SuccessModal';
import ImportFa3SemanticPreview from '../Components/ImportFa3SemanticPreview';
import InvoiceDirectionBadge from '../Components/InvoiceDirectionBadge';
import {
  useGetImportStatusQuery,
  useApproveImportMutation,
  useRejectImportMutation,
  useRetryImportMutation,
  useAbandonImportMutation,
  useDownloadImportFileMutation,
} from '../Api/invoiceImportApi';
import {
  InvoiceImportStatus,
  type ApproveImportSubmission,
} from '../Types/invoiceImport.types';
import { UserInvoiceCorrections } from '../Types/userInvoiceCorrections';
import { ExpenseInvoicesRoutes } from '../../../Router/path';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import {
  getInvoiceDirectionDecision,
  getMatchedSubjectRoleLabel,
} from '../Utils/invoiceDirection';

const getApproveErrorMessage = (error: unknown): string => {
  const fallback = extractApiErrorMessage(error, 'Nieznany błąd');
  const data =
    error && typeof error === 'object' && 'data' in error
      ? (error as { data?: unknown }).data
      : null;
  const errorCode =
    data &&
    typeof data === 'object' &&
    'errorCode' in data &&
    typeof (data as { errorCode?: unknown }).errorCode === 'string'
      ? (data as { errorCode: string }).errorCode
      : null;

  switch (errorCode) {
    case 'directionSalesNotExpense':
      return 'Dokument sprzedażowy nie może zostać zatwierdzony do ścieżki kosztowej.';
    case 'directionUnknownOverrideRequired':
      return 'Dla kierunku unknown zaznacz jawne override i podaj powód.';
    case 'directionUnknownOverrideReasonRequired':
      return 'Powód override jest wymagany dla kierunku unknown.';
    case 'directionUnknownOverrideAuditContextRequired':
      return 'Brak kontekstu audytowego override. Odśwież widok i spróbuj ponownie.';
    default:
      return fallback;
  }
};

/**
 * Detail page for a single import job
 * Displays full import metadata, status tracking, and actions
 * Phase 4: Implements approve/reject/retry/download actions
 */
const ImportJobDetailPage: React.FC = () => {
  const { importJobId } = useParams<{ importJobId: string }>();
  const navigate = useNavigate();

  const storedOcrCorrections: UserInvoiceCorrections | null = React.useMemo(() => {
    if (!importJobId) return null;

    try {
      const raw = sessionStorage.getItem(`invoiceImport:ocrCorrections:${importJobId}`);
      if (!raw) return null;
      return JSON.parse(raw) as UserInvoiceCorrections;
    } catch {
      return null;
    }
  }, [importJobId]);

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [abandonDialogOpen, setAbandonDialogOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successData, setSuccessData] = useState<{
    invoiceId: number | null;
    expenseInvoiceId: number | null;
    invoiceNumber: string | null;
    message: string | null;
  }>({ invoiceId: null, expenseInvoiceId: null, invoiceNumber: null, message: null });

  const {
    data: importStatus,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetImportStatusQuery(Number(importJobId), {
    skip: !importJobId,
  });

  const [approveImport, { isLoading: isApproving }] = useApproveImportMutation();
  const [rejectImport, { isLoading: isRejecting }] = useRejectImportMutation();
  const [retryImport, { isLoading: isRetrying }] = useRetryImportMutation();
  const [abandonImport, { isLoading: isAbandoning }] = useAbandonImportMutation();
  const [triggerDownload] = useDownloadImportFileMutation();

  const handleBackToHistory = () => {
    navigate(ExpenseInvoicesRoutes.importHistory);
  };

  const handleBackToImport = () => {
    navigate(ExpenseInvoicesRoutes.import);
  };

  const handleDownloadFile = async () => {
    if (!importJobId) return;

    try {
      const result = await triggerDownload(Number(importJobId)).unwrap();
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = importStatus?.fileName || 'invoice-import.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Plik został pobrany');
    } catch (err: unknown) {
      toast.error(`Nie udało się pobrać pliku: ${extractApiErrorMessage(err, 'Nieznany błąd')}`);
    }
  };

  const handleRetry = async () => {
    if (!importJobId) return;

    try {
      const result = await retryImport(Number(importJobId)).unwrap();
      toast.success('Import uruchomiony ponownie');
      // Navigate to the new retry attempt's job id — the old job is now a terminal
      // Failed leaf and its status will never change again (RM-09/S5b stop condition).
      navigate(`/expense-invoices/import/${result.importJobId}`);
    } catch (err: unknown) {
      toast.error(`Nie udało się ponowić importu: ${extractApiErrorMessage(err, 'Nieznany błąd')}`);
    }
  };

  const handleApproveClick = () => {
    if (!importStatus) {
      return;
    }

    // Deliberately does NOT gate on `allowedActions.includes('approve')`: that flag reflects
    // whether the *raw, uncorrected* parsed data currently has a blocking issue. The approve
    // endpoint (ApproveImportCommandHandler) applies the modal's user corrections BEFORE running
    // that same validation, so a blocking issue on the raw data is exactly the case the modal
    // exists to resolve — gating the modal open on it would make blocking issues unfixable.
    const decision = getInvoiceDirectionDecision(importStatus.invoiceDirection, importStatus.importerType);
    if (!decision.canOpenApproveModal) {
      return;
    }

    setApproveModalOpen(true);
  };

  const handleRejectClick = () => {
    setRejectModalOpen(true);
  };

  const handleApprove = async ({
    userCorrections,
    approveOverride,
    approveOverrideReason,
  }: ApproveImportSubmission) => {
    if (!importJobId) return;

    try {
      const result = await approveImport({
        importJobId: Number(importJobId),
        userCorrections,
        approveOverride,
        approveOverrideReason,
      }).unwrap();

      setApproveModalOpen(false);
      setSuccessData({
        invoiceId: result.invoiceId,
        expenseInvoiceId: result.expenseInvoiceId || null,
        invoiceNumber: result.invoiceNumber,
        message: result.message,
      });
      setSuccessModalOpen(true);
      toast.success('Import zatwierdzony!');

      try {
        sessionStorage.removeItem(`invoiceImport:ocrCorrections:${importJobId}`);
      } catch {}

      refetch();
    } catch (err: unknown) {
      const errorMessage = getApproveErrorMessage(err);
      toast.error(`Nie udało się zatwierdzić importu: ${errorMessage}`);
    }
  };

  const handleReject = async (reason: string) => {
    if (!importJobId) return;

    try {
      await rejectImport({
        importJobId: Number(importJobId),
        reason,
      }).unwrap();

      toast.success('Import odrzucony');
      setRejectModalOpen(false);
      refetch();
    } catch (err: unknown) {
      const errorMessage = extractApiErrorMessage(err, 'Nieznany błąd');
      toast.error(`Nie udało się odrzucić importu: ${errorMessage}`);
    }
  };

  const handleAbandonClick = () => {
    setAbandonDialogOpen(true);
  };

  const handleAbandon = async () => {
    if (!importJobId) return;

    try {
      await abandonImport(Number(importJobId)).unwrap();

      toast.success('Import porzucony');
      setAbandonDialogOpen(false);
      refetch();
    } catch (err: unknown) {
      const errorMessage = extractApiErrorMessage(err, 'Nieznany błąd');
      toast.error(`Nie udało się porzucić importu: ${errorMessage}`);
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body1">Ładowanie szczegółów importu...</Typography>
        </Box>
      </Container>
    );
  }

  if (isError || !importStatus) {
    const errorMessage = extractApiErrorMessage(error, 'Nie znaleziono importu');

    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={handleBackToHistory}>
                Powrót do historii
              </Button>
            }
          >
            {errorMessage}
          </Alert>
        </Box>
      </Container>
    );
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // Render only from the backend contract (RM-09) — never re-derive eligibility from status
  // alone. Duplicate always gets an empty allowedActions list, so it correctly never offers retry.
  // Exception: 'approve' is deliberately NOT read here. It reflects whether the raw, uncorrected
  // parsed data currently has a blocking issue, but the approve endpoint applies the correction
  // modal's user corrections before validating — so it is not a valid signal for whether the
  // modal itself should be reachable (see handleApproveClick).
  const allowedActions = importStatus.allowedActions ?? [];
  const canRetry = allowedActions.includes('retry');
  const canReject = allowedActions.includes('reject');
  const canAbandon = allowedActions.includes('abandon');
  const directionDecision = getInvoiceDirectionDecision(importStatus.invoiceDirection, importStatus.importerType);
  const matchedSubjectRoleLabel = importStatus.matchedSubjectRole
    ? getMatchedSubjectRoleLabel(importStatus.matchedSubjectRole)
    : null;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="h5" gutterBottom>
                Szczegóły importu #{importJobId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {importStatus.fileName}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button startIcon={<ArrowBackIcon />} onClick={handleBackToHistory} variant="text">
                Historia
              </Button>
              <Button startIcon={<RefreshIcon />} onClick={() => refetch()} variant="outlined">
                Odśwież
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleDownloadFile}
                variant="outlined"
                disabled={!importStatus.blobStoragePath}
              >
                Pobierz plik
              </Button>
            </Stack>
          </Box>
        </Paper>

        {/* Progress Tracker */}
        <Box sx={{ mb: 3 }}>
          <ImportProgressTracker
            importJobId={Number(importJobId)}
            autoRedirect={false}
            onStatusChange={(status) => {
              // Could trigger notifications
            }}
          />
        </Box>

        {/* Metadata Panel */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Metadane importu
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }} >
              <Typography variant="subtitle2" color="text.secondary">
                Nazwa pliku
              </Typography>
              <Typography variant="body1" gutterBottom>
                {importStatus.fileName}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} >
              <Typography variant="subtitle2" color="text.secondary">
                Typ MIME
              </Typography>
              <Typography variant="body1" gutterBottom>
                {importStatus.contentType}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} >
              <Typography variant="subtitle2" color="text.secondary">
                Rozmiar pliku
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formatBytes(importStatus.fileSizeBytes)}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} >
              <Typography variant="subtitle2" color="text.secondary">
                Typ importera
              </Typography>
              <Typography variant="body1" gutterBottom>
                {importStatus.importerType || '—'}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} >
              <Typography variant="subtitle2" color="text.secondary">
                Ścieżka Blob Storage
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'action.hover',
                  p: 0.5,
                  borderRadius: 1,
                  wordBreak: 'break-all',
                }}
              >
                {importStatus.blobStoragePath}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} >
              <Typography variant="subtitle2" color="text.secondary">
                Numer faktury
              </Typography>
              <Typography variant="body1" component="div" gutterBottom>
                {importStatus.invoiceNumber ? (
                  <Chip label={importStatus.invoiceNumber} color="primary" size="small" />
                ) : (
                  '—'
                )}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Kierunek dokumentu
              </Typography>
              <Stack spacing={0.75}>
                <InvoiceDirectionBadge
                  direction={importStatus.invoiceDirection}
                  source={importStatus.invoiceDirectionSource}
                />
                <Typography variant="caption" color="text.secondary">
                  {directionDecision.message}
                </Typography>
                {matchedSubjectRoleLabel ? (
                  <Typography variant="caption" color="text.secondary">
                    Dopasowana rola: {matchedSubjectRoleLabel}
                  </Typography>
                ) : null}
                {importStatus.invoiceDirectionReason ? (
                  <Typography variant="caption" color="text.secondary">
                    Powód klasyfikacji: {importStatus.invoiceDirectionReason}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <ImportFa3SemanticPreview
          preview={importStatus.fa3SemanticPreview}
          parserMetadata={importStatus.parsedInvoiceData?.parserMetadata}
        />

        {/* Actions for RequiresReview */}
        {importStatus.status === InvoiceImportStatus.RequiresReview && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Alert severity={directionDecision.severity} sx={{ mb: 2 }}>
              {directionDecision.message}
            </Alert>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                onClick={handleApproveClick}
                disabled={isApproving || !directionDecision.canOpenApproveModal}
              >
                {directionDecision.canOpenApproveModal
                  ? isApproving
                    ? 'Zatwierdzanie...'
                    : 'Weryfikuj i zatwierdź'
                  : 'Zatwierdzenie zablokowane'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleRejectClick}
                disabled={isRejecting || !canReject}
              >
                {isRejecting ? 'Odrzucanie...' : 'Odrzuć import'}
              </Button>
              {canAbandon && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleAbandonClick}
                  disabled={isAbandoning}
                >
                  {isAbandoning ? 'Porzucanie...' : 'Porzuć import'}
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {/* Actions for Rejected. Only abandon is ever offered here, driven strictly by the
            backend contract (RM-06/I6) — a rejected import has no other write-side transition. */}
        {importStatus.status === InvoiceImportStatus.Rejected && canAbandon && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Import został odrzucony. Możesz go porzucić, aby odblokować ponowny import tego
              samego pliku.
            </Alert>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleAbandonClick}
                disabled={isAbandoning}
              >
                {isAbandoning ? 'Porzucanie...' : 'Porzuć import'}
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Actions for Failed. The status check here is deliberate, not a re-derivation of action
            eligibility (RM-09): the "Import zakończył się niepowodzeniem" copy is Failed-specific,
            not generic. Button availability itself still comes only from canRetry/canReject. */}
        {importStatus.status === InvoiceImportStatus.Failed && (canRetry || canReject) && (
          <Paper sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Import zakończył się niepowodzeniem.
              {canRetry ? ' Możesz spróbować ponownie.' : ''}
            </Alert>
            <Stack direction="row" spacing={2}>
              {canRetry && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={handleRetry}
                  disabled={isRetrying}
                >
                  {isRetrying ? 'Ponowienie...' : 'Spróbuj ponownie'}
                </Button>
              )}
              {canReject && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleRejectClick}
                  disabled={isRejecting}
                >
                  {isRejecting ? 'Odrzucanie...' : 'Odrzuć import'}
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {/* Back to import button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={handleBackToImport}>
            Nowy import
          </Button>
        </Box>
      </Box>

      {/* Modals */}
      <ApproveImportModal
        open={approveModalOpen}
        onClose={() => !isApproving && setApproveModalOpen(false)}
        importStatus={importStatus}
        initialCorrections={storedOcrCorrections}
        onApprove={handleApprove}
        isLoading={isApproving}
      />
      <RejectImportModal
        open={rejectModalOpen}
        onClose={() => !isRejecting && setRejectModalOpen(false)}
        fileName={importStatus.fileName}
        onReject={handleReject}
        isLoading={isRejecting}
      />
      <SuccessModal
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        invoiceId={successData.invoiceId}
        expenseInvoiceId={successData.expenseInvoiceId}
        invoiceNumber={successData.invoiceNumber}
        message={successData.message}
      />
      <Dialog
        open={abandonDialogOpen}
        onClose={isAbandoning ? undefined : () => setAbandonDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isAbandoning}
      >
        <DialogTitle>
          <Typography variant="h6" component="span" display="block">
            Porzuć import
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plik: {importStatus.fileName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            Import zostanie oznaczony jako porzucony. Plik, dane OCR i historia zostaną
            zachowane — nic nie zostanie usunięte. Po porzuceniu będzie można ponownie
            zaimportować ten sam plik jako nowy, świeży import.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAbandonDialogOpen(false)} disabled={isAbandoning}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleAbandon}
            disabled={isAbandoning}
            startIcon={isAbandoning ? <CircularProgress size={16} /> : undefined}
          >
            {isAbandoning ? 'Porzucanie...' : 'Porzuć import'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ImportJobDetailPage;
